import React, { useState } from "react";
import {
  Button,
  Drawer,
  Form,
  Input,
  Select,
  Checkbox,
  Switch,
  Table,
  Tooltip,
  FormInstance,
} from "antd";
import {
  PlusCircleOutlined,
  InfoCircleOutlined,
} from "@ant-design/icons";

import { groupAndSortByKey } from "../../common/utils/helpers";

import type { CheckboxChangeEvent } from "antd/es/checkbox";
import { useDrawerScrollMargin } from "common/utils/useScrollPosition";
import { Application, ClientPermission, Permission } from "./types/apiClients";
import ActionButton from "stories/components/ActionButton/ActionButton";
const { Option } = Select;
const { TextArea } = Input;

type PermissionKey = "read" | "create" | "edit" | "delete";

export interface NewClientApiProps {
  visible: boolean;
  form: FormInstance;
  permissionsList: Permission[];
  applicationsList: Application[];
  hasPermissions: boolean;
  onOpen: () => void;
  onClose: () => void;
  onRefresh: () => void;
  onFinish: (values: any, permissions: ClientPermission[]) => void;
}

const NewClientApi: React.FC<NewClientApiProps> = ({
  visible,
  form,
  permissionsList,
  applicationsList,
  onOpen,
  onClose,
  onRefresh,
  onFinish,
  hasPermissions
}) => {
  const [displayedPermissions, setDisplayedPermissions] =
    useState<Permission[]>(permissionsList);
  const [updatedPermissions, setUpdatedPermissions] = useState<
    ClientPermission[]
  >([]);
  useDrawerScrollMargin('', visible);
  const onChangePermission = (
    e: CheckboxChangeEvent,
    id: string
  ) => {
    const name = e.target.name as PermissionKey | undefined;
    const checked = e.target.checked;
    if (!name) return;

    setUpdatedPermissions((prev) =>
      prev.map((p) =>
        p.permissionId === id
          ? { ...p, [name]: checked }
          : p
      )
    );
  };

  const permissionColumns = [
    {
      title: "",
      dataIndex: "name",
      key: "name",
      width: 150,
    },
    {
      title: "",
      dataIndex: "description",
      key: "description",
      render: (_: any, record: Permission) => (
        <Tooltip
          title={record.description}
          color="var(--tertiary-color)"
          placement="left"
        >
          <InfoCircleOutlined style={{ color: "var(--tertiary-color)" }} />
        </Tooltip>
      ),
    },
    ...(["Read", "Create", "Edit", "Delete"] as const).map((perm) => {
      const supportKey = `supports${perm}` as keyof Permission;
      const dataKey = perm.toLowerCase() as keyof ClientPermission;
      return {
        title: perm,
        dataIndex: dataKey,
        key: dataKey,
        render: (_: any, record: Permission) =>
          record[supportKey] ? (
            <Checkbox
              name={dataKey}
              defaultChecked={false}
              onChange={(e: CheckboxChangeEvent) =>
                onChangePermission(e, record.id)
              }
            />
          ) : (
            <Checkbox name={dataKey} disabled />
          ),
      };
    }),
  ];

  const grouped = groupAndSortByKey(displayedPermissions, "category");
  const renderedPermissions = Object.entries(grouped).map(
    ([category, list]) => (
      <Table<Permission>
        key={category}
        columns={permissionColumns}
        // @ts-ignore
        dataSource={list?.sort((a:any, b:any) => a.name.localeCompare(b.name))}
        title={() => category}
        pagination={false}
        className="role-table"
      />
    )
  );

  const handleAppChange = (value: string) => {
    const filtered = permissionsList.filter(
      (p) => p.customerApplication === value
    );
    setDisplayedPermissions(filtered);

    const initialPerms: ClientPermission[] = filtered.map((p) => ({
      permissionId: p.id,
      read: false,
      create: false,
      edit: false,
      delete: false,
    }));
    setUpdatedPermissions(initialPerms);
  };

  const closeDrawer = () => {
    form.resetFields();
    setDisplayedPermissions([]);
    setUpdatedPermissions([]);
    onClose();
  };

  return (
    <>
      <ActionButton
        onClick={onOpen}
        text="New Api Client"
        type="primary"
        disabled={!hasPermissions}
        icon={<PlusCircleOutlined style={{ fontSize: 15 }} />
        }
      />
      <Drawer
        title="Add New Client Api"
        placement="right"
        onClose={closeDrawer}
        open={visible}
        width={550}
        closable
        className="manage-pages-drawer"
        forceRender
      >
        <Form
          form={form}
          layout="vertical"
          onFinish={(vals) => onFinish(vals, updatedPermissions)}
          name="newClientApiForm"
          className="new-client-api-form"
          autoComplete="off"
          initialValues={{ isActiveValue: false }}
        >
          <Form.Item
            name="nameValue"
            label="Name"
            rules={[{ required: true, message: "Please type the name" }]}
          >
            <Input placeholder="Ex: Some Application" />
          </Form.Item>
          <Form.Item
            name="descriptionValue"
            label="Description"
            rules={[{ required: true, message: "Please type the description" }]}
          >
            <TextArea
              rows={4}
              placeholder="Ex: Provide access for some application."
            />
          </Form.Item>
          <Form.Item
            label="Active"
            name="isActiveValue"
            valuePropName="checked"
          >
            <Switch />
          </Form.Item>
          <Form.Item
            label="Customer Application"
            name="customerApplicationValue"
            rules={[
              { required: true, message: "Please select an application" },
            ]}
          >
            <Select placeholder="Select application" onChange={handleAppChange}>
              {applicationsList
                .filter((app) => app.active)
                .map((app) => (
                  <Option key={app._id} value={app._id}>
                    {app.name}
                  </Option>
                ))}
            </Select>
          </Form.Item>

          <Form.Item label="Permissions">
            {form.isFieldTouched("customerApplicationValue") ? (
              displayedPermissions.length > 0 ? (
                renderedPermissions
              ) : (
                <p>No permissions for this application.</p>
              )
            ) : (
              <p>Please select an application to view permissions.</p>
            )}
          </Form.Item>

          <Form.Item>
            <Button type="primary" htmlType="submit">
              Save Client Api
            </Button>
          </Form.Item>
        </Form>
      </Drawer>
    </>
  );
};

export default NewClientApi;
